import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';

import { selectUser } from 'entities/user/selectors';
import User from 'entities/user/model';
import { RenderMachine, StepComponentProps } from 'components/Flow/RenderMachine';
import OrganizationApi from 'entities/organization/api';
import { usePayPalVerificationCode } from 'PayPalVerificationCodeProvider';
import { updatePropertyAction } from 'entities/user/actions';

import { verificationMachine } from './machine';
import { getVerificationMachineContext } from './getVerificationMachineContext';
import { redirectToPayPalVerification } from './redirectToPayPalVerification';

export const NonprofitOnboardingVerification = (props: StepComponentProps) => {
  const apolloClient = useApolloClient();
  const user = useSelector(selectUser) as User;
  const dispatch = useDispatch();

  const payPalVerificationCode = usePayPalVerificationCode();

  if (!user?.organization) {
    return null;
  }
  const isCompany = user.organization?.isCompany();
  if (isCompany) {
    return null;
  }

  return (
    <RenderMachine
      machine={verificationMachine}
      options={{
        context: {
          ...getVerificationMachineContext(user, payPalVerificationCode, async () => {
            if (payPalVerificationCode) {
              const action = await OrganizationApi.verifyNonprofitWithPaypal(
                payPalVerificationCode
              ).toPromise();
              await apolloClient.refetchQueries({
                updateCache(cache) {
                  cache.evict({ fieldName: 'nonprofitInfo' });
                },
              });
              dispatch(action);
              return {
                privileges: action.user.privileges,
                nonprofit: {
                  payPalModel: action.user.organization?.payPalModel,
                  paypalCharityType: action.user.organization?.paypalCharityType,
                  nonprofitEntityType: action.user.organization?.nonprofitEntityType,
                  nonprofitDataValidatedAt: action.user.organization?.nonprofitDataValidatedAt,
                },
              };
            }
            return { privileges: [], nonprofit: {} };
          }),
          standaloneMode: props.standaloneMode,
        },
        id: props.id,
        actions: {
          handleFinal: () => props.send('NEXT'),
          redirectToPayPalVerification,
          setProofOfAssociation: (_context, event) => {
            dispatch(
              updatePropertyAction(
                ['additionalData', 'proofOfAssociation'],
                [{ file: event, nonprofitId: user.organization.id }]
              )
            );
          },
        },
      }}
    />
  );
};
